import React, { useEffect, useRef, useState } from "react";
import { Send, Bot, User as UserIcon, Inbox, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, PageHeader, Badge, Button } from "@/components/ui-kit";

/**
 * Iter v31 — Admin/HR inbox for karyawan_cs chat threads.
 * Lists all karyawan_cs threads; click to view + reply.
 */

function fmtTime(ts) {
  if (!ts) return "";
  try { return new Date(ts).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}
function fmtDate(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return fmtTime(ts);
    return d.toLocaleDateString("id-ID", { day: "2-digit", month: "short" });
  } catch { return ""; }
}

export default function KaryawanCSInbox() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  // Iter v34r (#1) — Only scroll to bottom when message count INCREASES (new message),
  // not on every poll refresh. Use container-bound scroll, not page-level scrollIntoView.
  const prevMsgCount = useRef(0);
  const messagesContainerRef = useRef(null);

  const loadThreads = async () => {
    try { const r = await api.get("/chat/karyawan-cs/threads"); setThreads(r.data || []); }
    catch { /* */ }
  };
  const loadMessages = async (tid) => {
    if (!tid) return;
    try { const r = await api.get(`/chat/threads/${tid}/messages`); setMessages(r.data || []); }
    catch { /* */ }
  };

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { if (activeId) loadMessages(activeId); }, [activeId]);
  // Poll
  useEffect(() => {
    const t = setInterval(() => { loadThreads(); if (activeId) loadMessages(activeId); }, 6000);
    return () => clearInterval(t);
  }, [activeId]);
  useEffect(() => {
    // Only scroll when message count actually grows (new message arrived).
    // Use container scroll (not scrollIntoView) to prevent the WHOLE PAGE from scrolling.
    if (messages.length > prevMsgCount.current) {
      const el = messagesContainerRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
    prevMsgCount.current = messages.length;
  }, [messages]);
  // Reset count when switching threads
  useEffect(() => { prevMsgCount.current = 0; }, [activeId]);

  const send = async () => {
    if (!input.trim() || !activeId || sending) return;
    setSending(true);
    try {
      await api.post(`/chat/threads/${activeId}/messages`, { body: input.trim() });
      setInput("");
      await loadMessages(activeId);
      loadThreads();
    } finally { setSending(false); }
  };

  // Iter v34x (#5) — Admin/HR close chat HR thread.
  const closeThread = async () => {
    if (!activeId) return;
    const reason = window.prompt("Alasan menutup chat (opsional):", "");
    if (reason === null) return;
    try {
      await api.post(`/chat/karyawan-cs/threads/${activeId}/close`, { reason });
      setActiveId(null);
      setMessages([]);
      loadThreads();
    } catch (e) {
      console.error(e);
    }
  };

  const active = threads.find((t) => t.id === activeId);

  return (
    <div className="min-h-full bg-[#f6f5f0]">
    <div className="max-w-[1400px] mx-auto p-4 md:p-6 lg:p-8 space-y-4" data-testid="karyawan-cs-inbox-page">
      <PageHeader overline="Admin · HR" title="Inbox Chat Karyawan"
        description="Tanggapi pertanyaan karyawan seputar absensi & gaji. Auto-eskalasi muncul di sini." />

      <Card className="p-0 flex flex-col md:flex-row" style={{ minHeight: "60vh" }}>
        {/* Thread list */}
        <div className="md:w-72 border-r border-stone-200 flex flex-col">
          <div className="px-3 py-2 border-b border-stone-100 text-[11px] uppercase tracking-widest text-stone-500 flex items-center gap-1">
            <Inbox className="h-3 w-3" /> {threads.length} Percakapan
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="karyawan-cs-inbox-list">
            {threads.length === 0 && (
              <div className="p-8 text-center text-xs text-stone-400">Belum ada percakapan.</div>
            )}
            {threads.map((t) => (
              <button key={t.id} onClick={() => setActiveId(t.id)}
                className={`w-full text-left px-3 py-2.5 border-b border-stone-50 hover:bg-stone-50 ${activeId === t.id && "bg-terra-50"}`}
                data-testid={`karyawan-cs-thread-${t.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-medium text-sm text-stone-900 truncate">{t.karyawan_name || "Karyawan"}</div>
                  {t.unread_count > 0 && <span className="bg-blue-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">{t.unread_count}</span>}
                </div>
                {/* Iter v33 — Show department + position */}
                {(t.karyawan_department || t.karyawan_position) && (
                  <div className="text-[10px] text-stone-500 flex items-center gap-1.5 mt-0.5">
                    {t.karyawan_department && <span className="bg-stone-100 px-1.5 py-0.5">{t.karyawan_department}</span>}
                    {t.karyawan_position && <span>{t.karyawan_position}</span>}
                  </div>
                )}
                <div className="text-xs text-stone-500 truncate mt-0.5">
                  {t.last_message?.is_bot && <Sparkles className="h-3 w-3 inline mr-0.5 text-terra-400" />}
                  {t.last_message?.body?.slice(0, 60) || "—"}
                </div>
                <div className="text-[10px] text-stone-400 mt-0.5">{fmtDate(t.last_message_at)}</div>
              </button>
            ))}
          </div>
        </div>
        {/* Chat panel */}
        <div className="flex-1 flex flex-col">
          {!active && (
            <div className="flex-1 flex items-center justify-center text-stone-400 text-sm">
              Pilih percakapan dari daftar.
            </div>
          )}
          {active && (
            <>
              <div className="px-4 py-2 border-b border-stone-200 text-sm font-medium text-stone-900 flex items-center gap-2 flex-wrap">
                <span>{active.karyawan_name || "Karyawan"}</span>
                {active.karyawan_department && <span className="text-[10px] bg-stone-100 px-2 py-0.5">{active.karyawan_department}</span>}
                {active.karyawan_position && <span className="text-[11px] text-stone-500">· {active.karyawan_position}</span>}
                {active.karyawan_code && <span className="text-[10px] text-stone-400 font-mono">({active.karyawan_code})</span>}
              </div>
              <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: "55vh" }}>
                {messages.length === 0 && <div className="text-center text-xs text-stone-400 py-8">Belum ada pesan.</div>}
                {messages.map((m) => {
                  const isMe = m.sender_id === user?.id;
                  const isBot = m.is_bot || m.sender_id === "ai_assistant";
                  const isKaryawan = !isMe && !isBot;
                  return (
                    <div key={m.id} className={`flex gap-2 ${isMe ? "justify-end" : "justify-start"}`}>
                      {!isMe && (
                        <div className={`w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center ${isBot ? "bg-terra-100 text-terra-700" : "bg-stone-200 text-stone-700"}`}>
                          {isBot ? <Bot className="h-3.5 w-3.5" /> : <UserIcon className="h-3.5 w-3.5" />}
                        </div>
                      )}
                      <div className={`max-w-[70%] flex flex-col ${isMe ? "items-end" : "items-start"}`}>
                        {!isMe && (
                          <span className="text-[10px] text-stone-500 px-1 mb-0.5">
                            {isBot ? "Asisten HR (AI)" : (isKaryawan ? m.sender_name : `Admin (${m.sender_name || "?"})`)}
                          </span>
                        )}
                        <div className={`px-3 py-2 rounded-2xl text-sm ${isMe ? "bg-blue-600 text-white" : (isBot ? "bg-blue-50 border border-blue-100 text-stone-800" : "bg-stone-100 text-stone-900")}`}>
                          <div className="whitespace-pre-wrap break-words">{m.body}</div>
                        </div>
                        <span className="text-[10px] text-stone-400 px-1 mt-0.5">{fmtTime(m.ts)}</span>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              <div className="border-t border-stone-200 p-3 flex gap-2">
                <input type="text" value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Balas pesan karyawan..."
                  className="flex-1 border border-stone-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  disabled={sending}
                  data-testid="karyawan-cs-reply-input" />
                <Button onClick={send} disabled={sending || !input.trim()} data-testid="karyawan-cs-reply-send">
                  <Send className="h-3.5 w-3.5" /> Balas
                </Button>
                {!active?.closed && (
                  <Button variant="ghost" onClick={closeThread} className="text-red-700 hover:bg-red-50 !border-red-200" title="Tutup chat" data-testid="karyawan-cs-close-thread">
                    Tutup
                  </Button>
                )}
                {active?.closed && (
                  <span className="px-2 py-1 text-[10px] uppercase tracking-wider bg-stone-100 text-stone-500 self-center">Closed</span>
                )}
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
    </div>
  );
}
